import type { PayloadAction } from '@reduxjs/toolkit';
import OnboardingTip from '@rio-cloud/rio-uikit/OnboardingTip';
import type { Placement } from '@rio-cloud/rio-uikit/values/Placement';
import type React from 'react';
import { injectIntl, type WrappedComponentProps } from 'react-intl';
import { connect } from 'react-redux';
import type { Dispatch } from 'redux';

import type { State } from '../../reducers';
import { getShowOnboardingTip, onboardingActions } from './redux/oboarding.redux';
import type { Tips } from './types';

interface OnboardingTipWrapperStateProps {
    showOnboardingTip: (tip: Tips) => boolean;
}

interface OnboardingTipWrapperDispatchProps {
    hideOnboardingTip: (tip: Tips) => PayloadAction<{ type: Tips; value: boolean }>;
}

interface OnboardingTipWrapperProps {
    tipType: Tips;
    titleTranslationId: string;
    contentTranslationId: string;
    children: React.ReactNode;
    className?: string;
    placement?: Placement;
    isEnabled?: boolean;
}

export const OnboardingTipWrapper = (
    props: OnboardingTipWrapperStateProps &
        OnboardingTipWrapperDispatchProps &
        OnboardingTipWrapperProps &
        WrappedComponentProps
): JSX.Element => {
    const {
        children,
        intl,
        tipType,
        titleTranslationId,
        contentTranslationId,
        hideOnboardingTip,
        showOnboardingTip,
        className = 'max-width-200',
        placement = 'bottom',
    } = props;

    const onHide = (): PayloadAction<{ type: Tips; value: boolean }> => hideOnboardingTip(tipType);
    const isEnabled = props.isEnabled ?? true;

    return (
        <OnboardingTip
            show={isEnabled && showOnboardingTip(tipType)}
            placement={placement}
            title={intl.formatMessage({ id: titleTranslationId })}
            content={intl.formatMessage({ id: contentTranslationId })}
            className={className}
            onHide={onHide}
        >
            {children}
        </OnboardingTip>
    );
};

const mapStateToProps = (state: State): OnboardingTipWrapperStateProps => ({
    showOnboardingTip: (tip: Tips): boolean => getShowOnboardingTip(state, tip),
});

const mapDispatchToProps = (dispatch: Dispatch): OnboardingTipWrapperDispatchProps => ({
    hideOnboardingTip: (tip: Tips): PayloadAction<{ type: Tips; value: boolean }> =>
        dispatch(onboardingActions.setShowOnboardingTip({ type: tip, value: false })),
});

export const ConnectedOnboardingTip = connect(mapStateToProps, mapDispatchToProps)(injectIntl(OnboardingTipWrapper));
